// React
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

// Components
import { DurationsDesktop } from './Desktop'
import { DurationsMobile } from './Mobile'

// Libraries
import { useFormikContext } from 'formik'

// Hooks
import { useDurations, usePicker } from 'ui/components/Picker/hooks'
import { useOrm } from 'shared/utils/useOrm'
import { useTranslation } from 'shared/utils/useTranslation'
import { useSettings } from 'shared/utils/useSettings'

// Shared
import useWindowSize from 'shared/utils/useWindowSize'
import { parseFixedDefaultTime } from 'shared/utils/time'
import { defaultDefaultTimes } from 'ui/components/Picker/utils'

export const Durations = ({ onChange: _onChange, ...props }) => {
  const { t } = useTranslation('user')
  const { width } = useWindowSize()

  const picker = usePicker()
  const form = useFormikContext()
  const startDefaultTimeSetting = useSettings('orders.start_fixed_at', { default: defaultDefaultTimes.start })
  const stopDefaultTimeSetting = useSettings('orders.stop_fixed_at', { default: defaultDefaultTimes.stop })

  const { clearCacheRef, duration, durationObject, onChange, getRange } = useDurations(props.onDurationChange)

  const durations = useOrm('operating_rules', (rules) => {
    return rules.getAll({ data_type: 'timeslot_duration' })
  })

  const isMobile = width < 576

  const from = form.values.starts_at ? new Date(form.values.starts_at) : null
  const to = form.values.stops_at ? new Date(form.values.stops_at) : null

  const options = useMemo(() => {
    return durations
      .sortBy((duration) => duration.data.length)
      .map((duration) => {
        const isPlural = duration.data.duration > 1

        return {
          value: duration.id,
          label: t('common.periods.count', {
            count: duration.data.duration,
            period: t(`user.store.${duration.data.period}${isPlural ? 's' : ''}`)
          })
        }
      })
      .toJS()
  }, [durations])

  const isReturnTimeDisabled = durationObject?.data?.period === 'hour' && duration <= 86400

  const getBlockedDays = (date) => {
    const durationData = durationObject?.data
    const defaultTimes = {
      start: parseFixedDefaultTime(startDefaultTimeSetting),
      stop: parseFixedDefaultTime(stopDefaultTimeSetting)
    }

    return !picker.isDaySelectable({ type: 'start', date, durationData, defaultTimes })
  }

  const handleChangeStartsAt = (date) => {
    const range = getRange(date, form.values, durationObject)

    form.setValues({
      ...form.values,
      starts_at: range.from.toJSON(),
      stops_at: range.to.toJSON(),
      charge_in_seconds: parseInt(duration)
    })
  }

  const handleBlurStartsAt = (date) => {
    if (getBlockedDays(date)) {
      date = picker.setDefaultTime({ type: 'start', forceDefaultTime: true })

      handleChangeStartsAt(date)
    }
  }

  const handleSelectDuration = (durationId) => {
    const durationObject = durations.toArray().find((duration) => duration.id === durationId)
    const duration = durationObject?.data.length

    onChange({
      duration,
      durationObject,
      changePeriod: true
    })

    picker.onChange('duration')
  }

  const sharedProps = {
    from,
    to,
    options,
    onChange: handleChangeStartsAt,
    onBlur: handleBlurStartsAt,
    onDurationChange: handleSelectDuration,
    duration,
    durationObject,
    getRange,
    getBlockedDays,
    isReturnTimeDisabled,
    width: props.inline ? '100%' : undefined,
    clearCacheRef
  }

  return isMobile ? <DurationsMobile {...props} {...sharedProps} /> : <DurationsDesktop {...props} {...sharedProps} />
}

Durations.propTypes = {
  onDurationChange: PropTypes.func,
  onChange: PropTypes.func,
  inline: PropTypes.bool
}
